// covered-by: frontends/carrie/src/utils/queries/__tests__/askPuntt.test.tsx
import {
  type GetAskPunttProjectResponse,
  type ListAskPunttProjectsResponse,
} from "@mg/schemas/src/christo/askPunttProject";
import { type QueryClient } from "@tanstack/react-query";

import { PLACEHOLDER_PROJECT_NAME } from "../placeholderProjectName";

// why: importing askPunttProjectKeys cycles through the chat hooks module
const projectQueryKey = (projectId: string) =>
  ["ask-puntt-projects", "project", projectId] as const;
const projectListsPrefix = ["ask-puntt-projects", "list"] as const;

type ChatTitleSource = {
  projectId?: string | null;
  projectChatKind?: string | null;
  title?: string | null;
};

/**
 * Copy a shared chat's context title onto a project that is still the
 * untitled default. A name the user chose is left in the cache.
 */
export function syncPlaceholderProjectNameFromChat(
  queryClient: QueryClient,
  chat: ChatTitleSource,
): void {
  const projectId = chat.projectId ?? "";
  const title = chat.title?.trim() ?? "";
  if (
    !projectId ||
    chat.projectChatKind !== "shared" ||
    !title ||
    title === PLACEHOLDER_PROJECT_NAME
  ) {
    return;
  }

  const projectKey = projectQueryKey(projectId);
  const current =
    queryClient.getQueryData<GetAskPunttProjectResponse>(projectKey);
  // why: the detail guard only protects a cached name the user chose. An
  // uncached detail query (the common case on the home/feed surface) must
  // still fall through to the list loop, which re-checks every row itself.
  if (current && current.project.name !== PLACEHOLDER_PROJECT_NAME) return;

  if (current) {
    queryClient.setQueryData<GetAskPunttProjectResponse>(projectKey, {
      ...current,
      project: { ...current.project, name: title },
    });
  }

  const lists = queryClient.getQueriesData<ListAskPunttProjectsResponse>({
    queryKey: projectListsPrefix,
  });
  for (const [key, value] of lists) {
    if (!value?.projects) continue;
    if (
      !value.projects.some(
        (project) =>
          project._id === projectId &&
          project.name === PLACEHOLDER_PROJECT_NAME,
      )
    ) {
      continue;
    }
    queryClient.setQueryData<ListAskPunttProjectsResponse>(key, {
      ...value,
      projects: value.projects.map((project) =>
        project._id === projectId && project.name === PLACEHOLDER_PROJECT_NAME
          ? { ...project, name: title }
          : project,
      ),
    });
  }
}
